import React, { useState } from 'react'
import { wholesaleSections } from './content'
import type { Lang } from './types'

interface WholesaleAccordionSectionV2Props {
  currentLang: Lang
}

/**
 * SEO-friendly expandable sections.
 *
 * KEY: <h2> headers are ALWAYS rendered and visible.
 * The <p> body is also ALWAYS rendered in the DOM but visually collapsed
 * via max-height + opacity (never display:none, never lazy-fetched).
 *
 * This pattern is explicitly endorsed by Google:
 *   "Content in expandable sections is fully indexed and treated equally
 *    to visible content."
 *
 * All 5 sections start COLLAPSED per spec.
 */
const WholesaleAccordionSectionV2: React.FC<
  WholesaleAccordionSectionV2Props
> = ({ currentLang }) => {
  const [openIdx, setOpenIdx] = useState<number | null>(null)

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        {wholesaleSections.map((section, i) => {
          const isOpen = openIdx === i
          return (
            <div
              key={i}
              className="border-t border-black/15 last:border-b"
            >
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="flex w-full items-start justify-between gap-6 py-6 text-left transition-colors hover:bg-black/[0.02] md:py-8"
              >
                <h2
                  className="text-black"
                  style={{
                    fontFamily: "'EB Garamond', 'Noto Serif TC', serif",
                    fontWeight: 400,
                    fontSize: 'clamp(1.25rem, 2.2vw, 1.75rem)',
                    letterSpacing: '0.03em',
                    lineHeight: 1.35,
                  }}
                >
                  {section.title[currentLang]}
                </h2>
                <span
                  className="flex-shrink-0 text-2xl font-light text-black/55 transition-transform duration-300"
                  style={{
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                  }}
                  aria-hidden="true"
                >
                  +
                </span>
              </button>

              {/* Always rendered in DOM; only visually collapsed. */}
              <div
                className={`grid overflow-hidden transition-all duration-500 ease-out ${
                  isOpen
                    ? 'grid-rows-[1fr] opacity-100'
                    : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="min-h-0">
                  <p
                    className="pb-8 pr-10 text-[14px] leading-loose text-black/70 md:pr-20"
                    style={{
                      fontFamily:
                        "'Noto Serif TC', 'Noto Sans TC', sans-serif",
                      fontWeight: 300,
                      letterSpacing: '0.03em',
                    }}
                  >
                    {section.body[currentLang]}
                  </p>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

export default WholesaleAccordionSectionV2
